/**
 * Utilitário para cálculo de tokens baseado no modelo GPT-4o-mini
 * Aproximação: 1 token ≈ 4 caracteres para texto em português
 */

export interface TokenInfo {
    characters: number;
    tokens: number;
    maxTokens: number;
    remainingTokens: number;
    isOverLimit: boolean;
    percentage: number;
}

/**
 * Calcula informações de tokens para um texto
 * @param text Texto para calcular
 * @param maxTokens Limite máximo de tokens (padrão: 200000 para GPT-4o-mini com margem de segurança)
 * @returns Informações sobre tokens
 */
export const calculateTokenInfo = (text: string, maxTokens: number = 200000): TokenInfo => {
    const characters = text.length;
    const tokens = Math.ceil(characters / 4); // Aproximação conservadora
    const remainingTokens = Math.max(0, maxTokens - tokens);
    const isOverLimit = tokens > maxTokens;
    const percentage = Math.min(100, (tokens / maxTokens) * 100);

    return {
        characters,
        tokens,
        maxTokens,
        remainingTokens,
        isOverLimit,
        percentage
    };
};

/**
 * Valida se um texto está dentro do limite de tokens
 * @param text Texto para validar
 * @param maxTokens Limite máximo de tokens
 * @returns true se estiver dentro do limite
 */
export const validateTokenLimit = (text: string, maxTokens: number = 200000): boolean => {
    const tokenInfo = calculateTokenInfo(text, maxTokens);
    return !tokenInfo.isOverLimit;
};

/**
 * Formata informações de tokens para exibição
 * @param tokenInfo Informações de tokens
 * @returns String formatada para exibição
 */
export const formatTokenInfo = (tokenInfo: TokenInfo): string => {
    const { characters, tokens, maxTokens, remainingTokens, isOverLimit } = tokenInfo;

    if (isOverLimit) {
        return `${characters} caracteres | ${tokens}/${maxTokens} tokens (limite excedido)`;
    }

    return `${characters} caracteres | ${tokens}/${maxTokens} tokens (${remainingTokens} restantes)`;
};
